/**
 * Formats a phone number according to the following rules:
 * 1. Removes all non-numeric characters
 * 2. Ensures number starts with "27"
 * 3. Ensures total length is exactly 11 digits
 * 4. If number starts with "0", replaces it with "27"
 *
 * @param {string} phone - The phone number to format
 * @returns {string} - The formatted phone number or empty string if invalid
 *
 * Examples:
 * "0821234567" → "27821234567"
 * "821234567" → "27821234567"
 * "27821234567" → "27821234567"
 * "+27 82 123 4567" → "27821234567"
 * Invalid numbers return ""
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "";

  // Remove all non-numeric characters
  let cleaned = phone.replace(/\D/g, "");

  // If number starts with 27, just ensure it's 11 digits
  if (cleaned.startsWith("27")) {
    return cleaned.length === 11 ? cleaned : "";
  }

  // If number starts with 0, replace it with 27
  if (cleaned.startsWith("0")) {
    cleaned = "27" + cleaned.substring(1);
  } else if (!cleaned.startsWith("27")) {
    // If number doesn't start with 27 or 0, add 27
    cleaned = "27" + cleaned;
  }

  // Return only if the final number is exactly 11 digits
  return cleaned.length === 11 ? cleaned : "";
};

/**
 * Checks if a phone number is valid according to our format rules
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - True if the phone number is valid
 */
export const isValidPhoneNumber = (phone) => {
  const formatted = formatPhoneNumber(phone);
  return formatted !== "";
};

/**
 * Formats a phone number for display (with spaces)
 * @param {string} phone - The phone number to format
 * @returns {string} - The formatted phone number for display
 *
 * Example: "27821234567" → "27 82 123 4567"
 */
export const formatPhoneNumberForDisplay = (phone) => {
  const formatted = formatPhoneNumber(phone);
  if (!formatted) return phone; // Return original if invalid

  // Format as: 27 82 123 4567
  return formatted.replace(/(\d{2})(\d{2})(\d{3})(\d{4})/, "$1 $2 $3 $4");
};

/**
 * Local ZA mobile for `customer_contacts.telephone` / `.whatsapp`:
 * 10 digits, leading 0, no spaces (e.g. 0821234567).
 * @param {string} phone
 * @returns {string}
 */
export function formatCustomerContactLocalPhone(phone) {
  if (phone == null || phone === "") return "";

  let local = String(phone).trim();
  const at = local.indexOf("@");
  if (at > 0) local = local.slice(0, at);

  const msisdn = formatPhoneNumber(local);
  if (msisdn && msisdn.startsWith("27") && msisdn.length === 11) {
    return `0${msisdn.slice(2)}`;
  }

  const digits = local.replace(/\D/g, "");
  if (digits.startsWith("27") && digits.length >= 11) {
    return `0${digits.slice(2, 11)}`;
  }
  if (digits.startsWith("0") && digits.length >= 10) {
    return digits.slice(0, 10);
  }
  if (digits.length === 9) {
    return `0${digits}`;
  }

  return /^0\d{9}$/.test(digits) ? digits : "";
}

/**
 * Match stored `customer_contacts` values (local 0… or legacy 27… formats).
 * @param {string} phone
 * @returns {string[]}
 */
export function phoneLookupVariantsForContact(phone) {
  const variants = new Set();
  const local = formatCustomerContactLocalPhone(phone);
  if (local) {
    variants.add(local);
    const intl = `27${local.slice(1)}`;
    variants.add(intl);
    variants.add(`+${intl}`);
  }

  let raw = String(phone ?? "").trim();
  const at = raw.indexOf("@");
  if (at > 0) raw = raw.slice(0, at);
  const digits = raw.replace(/\D/g, "");
  if (digits) {
    variants.add(digits);
    if (digits.startsWith("27") && digits.length > 2) {
      variants.add(`0${digits.slice(2)}`);
    }
  }

  return [...variants];
}
